import { Dispatch, useRef } from "react";
import classNames from "classnames";
import { MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import prettyInstrument from "util/formatters/prettyInstrument";
import Button from "ui/atoms/Button/Button";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { motion } from "framer-motion";
import { State } from "../../state/reducer";
import Action from "../../state/actions";
import { isMuted, isSoloed } from "../../state/selectors";
import { isInstrumentCorrect } from "../../state/grader";
import PanKnob from "../../atoms/PanKnob/PanKnob";
import { difficultySettings } from "../../constants";
import MixPuzzleVolumeSlider from "../../atoms/MixPuzzleVolumeSlider";

interface Props {
  instrument: MixPuzzleInstrument;
  active: boolean | null;
  state: State;
  dispatch: Dispatch<Action>;
  afterSelectedCallback?: () => void;
}

export default function ChannelStrip(props: Props) {
  const { instrument, active, state, dispatch } = props;
  const ref = useRef<HTMLDivElement>(null);
  const correct = isInstrumentCorrect({
    attempt: instrument,
    desired: state.mixPuzzle.instruments[instrument.instrument],
    state,
  });
  const showGrade = difficultySettings.inGameTrackGrade(state);

  const onSelectInstrument = () => {
    props.afterSelectedCallback?.();

    dispatch({
      type: "updateSelectedInstrument",
      payload: instrument.instrument,
    });
  };

  return (
    <div
      ref={ref}
      className={classNames("relative z-0 flex h-40 rounded border-4", {
        "border-slate-300": !active,
        "border-white": active,
      })}
    >
      {active && (
        <motion.div
          initial={{ x: 0, y: "-50%" }}
          animate={{ x: "100%", y: "-50%" }}
          exit={{ x: 0, y: "-50%" }}
          className={"absolute right-0 top-1/2 -z-10 h-10 w-5 bg-white"}
        />
      )}

      <button
        onClick={onSelectInstrument}
        title={prettyInstrument(instrument.instrument)}
        className={classNames(
          "flex flex-col items-center justify-end py-2 pl-1 pr-1 transition-colors hover:bg-slate-300",
          active ? "bg-white text-black" : ""
        )}
      >
        <div
          style={{ writingMode: "vertical-rl" }}
          className="mb-2 rotate-180 overflow-hidden text-ellipsis whitespace-nowrap text-center font-brik leading-none tracking-wide"
        >
          {prettyInstrument(instrument.instrument, true)}
        </div>

        <div
          className={classNames("h-[24px] min-h-[24px] w-4 rounded-sm", {
            "bg-red-500": active,
            "bg-gray-500": !active,
          })}
        />
      </button>

      <hr
        className={classNames("h-full w-0.5 border-none", {
          "bg-slate-300": !active,
          "bg-white": active,
        })}
      />

      <div
        className={classNames("grid w-full p-2", {
          "bg-black": !active,
          "bg-purple-900 ": active,
        })}
      >
        <div className={"mb-4 flex items-center justify-between overflow-auto"}>
          <button
            onClick={onSelectInstrument}
            className={classNames(
              "mr-2 flex h-full flex-1 items-center overflow-auto whitespace-nowrap rounded border-2 py-1 pl-1 pr-3 text-left",
              showGrade
                ? { "border-green-500": correct, "border-red-500": !correct }
                : "pl-2"
            )}
          >
            {showGrade && <StatusIcon correct={correct} />}

            <div className={"font-brik text-xs leading-none"}>
              <div className="mr-2 inline-block">Vol: {instrument.volume}</div>
              <div className="inline-block">
                Comp: {instrument.comp === null ? "off" : instrument.comp}
              </div>

              <br />
              <div className="mr-2 inline-block">Pan: {instrument.pan}</div>
              <div className="inline-block">
                EQ: {instrument.eq_freq} {instrument.eq_gain}
              </div>
            </div>
          </button>

          <div className={"flex"}>
            <div className={"mr-2"}>
              <Button
                active={isSoloed(state, instrument.instrument)}
                theme="yellow"
                onClick={() =>
                  dispatch({
                    type: "updateSoloed",
                    payload: instrument.instrument,
                  })
                }
              >
                S
              </Button>
            </div>

            <div className={"mr-2"}>
              <Button
                active={isMuted(state, instrument.instrument)}
                theme="blue"
                onClick={() =>
                  dispatch({
                    type: "updateMuted",
                    payload: instrument.instrument,
                  })
                }
              >
                M
              </Button>
            </div>

            <Button
              theme={active ? "white" : "default"}
              active={active === null ? undefined : active}
              onClick={onSelectInstrument}
            >
              →
            </Button>
          </div>
        </div>

        <div className={"grid grid-cols-[1fr,75px] gap-2"}>
          <MixPuzzleVolumeSlider
            theme={active ? "white" : "default"}
            value={instrument.volume}
            onChange={(volume) =>
              dispatch({
                type: "requestPlaybackChange",
                payload: {
                  instrument: instrument.instrument,
                  params: { volume },
                },
              })
            }
          />

          <div className={"flex items-center justify-center"}>
            <PanKnob
              theme={active ? "white" : "default"}
              value={instrument.pan}
              onChange={(pan) =>
                dispatch({
                  type: "requestPlaybackChange",
                  payload: {
                    instrument: instrument.instrument,
                    params: { pan },
                  },
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusIcon({ correct }: { correct: boolean }) {
  const className = classNames("mr-1.5 w-6 min-w-[24px]", {
    "fill-green-500": correct,
    "fill-red-500": !correct,
  });

  if (correct) {
    return <CheckCircleIcon className={className} />;
  }

  return <XCircleIcon className={className} />;
}
